// Search sessions — the client half of Search Intents.
//
// Search-as-you-type sends a request per pause. Every request and pick made
// while looking for one thing carries the same session id, so the API can fold
// "tay", "tayl", "taylor" into one Search Intent
// (`contexts/analytics/server/src/db/search-intents.ts`). The id changes when the
// text stops being a continuation of what was typed before: "taylor" then
// "drake" is a second search, "taylor" then "tay" is the same one.
//
// Pure string helpers, zero dependencies (must run identically on Hermes and
// browsers).

// How many requested terms a search box remembers the session of.
const REMEMBERED_TERMS = 50;

const random = (): string => Math.random().toString(36).slice(2, 12);

const mintSearchSessionId = (): string =>
  `${Date.now().toString(36)}-${random()}-${random()}`;

const normalize = (term: string): string => term.trim().toLowerCase();

export type SearchSessions = {
  /** The session id to send with a search request for `term`. */
  forRequest: (term: string) => string;
  /**
   * The session id to send with a pick made on the results of `term`: the one
   * its request carried, even when the text has since moved on to another
   * search. A pick never starts a session.
   */
  forPick: (term: string) => string;
};

/** One tracker per search box. */
export const createSearchSessions = (): SearchSessions => {
  let id = mintSearchSessionId();
  let previous = "";
  const requested = new Map<string, string>();
  return {
    forRequest: (term) => {
      const next = normalize(term);
      if (next === "") return id;
      if (
        previous !== "" &&
        !next.startsWith(previous) &&
        !previous.startsWith(next)
      ) {
        id = mintSearchSessionId();
      }
      previous = next;
      requested.delete(next);
      requested.set(next, id);
      if (requested.size > REMEMBERED_TERMS) {
        requested.delete(requested.keys().next().value as string);
      }
      return id;
    },
    forPick: (term) => requested.get(normalize(term)) ?? id,
  };
};
